import { useMemo } from "react";
import { Box, Group, Text, Badge } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { useInsuranceProviders } from "../../../hooks/useReferenceData";

interface Props {
  caseData: any;
}

export default function InsuranceBillingCardV2({ caseData }: Props) {
  const primary = caseData?.primaryInsurance;
  const secondary = caseData?.secondaryInsurance;
  const scenario = caseData?.billingScenario;
  const primaryPlanType = caseData?.primaryInsurancePlanType;
  const secondaryPlanType = caseData?.secondaryInsurancePlanType;

  const { data: insuranceProviders } = useInsuranceProviders();

  const providerMap = useMemo(() => {
    const map = new Map<string, string>();
    insuranceProviders?.forEach((p) => map.set(p.value, p.name));
    return map;
  }, [insuranceProviders]);

  const primaryProviderName = primary?.insuranceProvider
    ? providerMap.get(primary.insuranceProvider)
    : undefined;
  const secondaryProviderName = secondary?.insuranceProvider
    ? providerMap.get(secondary.insuranceProvider)
    : undefined;

  const hasPrimary =
    primary &&
    (primary.insuranceProvider ||
      primary.policyNumber ||
      primary.groupNumber ||
      primary.groupName ||
      primary.authorizationNumber ||
      primary.patientRelationToInsured ||
      primaryPlanType);

  const hasSecondary =
    secondary &&
    (secondary.insuranceProvider ||
      secondary.policyNumber ||
      secondary.groupNumber ||
      secondary.groupName ||
      secondary.authorizationNumber ||
      secondary.patientRelationToInsured ||
      secondaryPlanType);

  return (
    <Box
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <Box
        px="lg"
        py="sm"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-1)",
          background: "rgba(248, 249, 250, 0.5)",
        }}
      >
        <Text size="sm" fw={700} tt="uppercase" lts="0.05em" c="dimmed">
          Insurance &amp; Billing
        </Text>
      </Box>
      <Box p="lg">
        <Group justify="space-between" mb="md">
          <Group gap={8}>
            <IconCircleCheck size={18} color="var(--mantine-color-green-5)" />
            <Text size="sm" fw={700}>
              {hasPrimary ? "Eligibility Active" : "No Insurance"}
            </Text>
          </Group>
          {scenario && (
            <Badge variant="light" color="gray" size="xs">
              {scenario.name || "AUTO-SCENARIO"}
            </Badge>
          )}
        </Group>

        {/* Primary Insurance */}
        <Box
          p="sm"
          mb="sm"
          style={{
            background: "var(--mantine-color-gray-0)",
            borderRadius: 8,
            border: "1px solid var(--mantine-color-gray-1)",
          }}
        >
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Primary Payor
          </Text>
          {hasPrimary ? (
            <>
              {primaryProviderName && (
                <Text size="sm" fw={700}>
                  {primaryProviderName}
                </Text>
              )}
              {primaryPlanType && (
                <Text size="xs" c="dimmed">
                  Plan: {primaryPlanType.name}
                </Text>
              )}
              <InsuranceDetails insurance={primary} />
            </>
          ) : (
            <Text size="sm" fw={500} fs="italic" c="dimmed">
              None provided
            </Text>
          )}
        </Box>

        {/* Secondary Insurance */}
        <Box
          p="sm"
          style={{
            background: "var(--mantine-color-gray-0)",
            borderRadius: 8,
            border: "1px solid var(--mantine-color-gray-1)",
          }}
        >
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Secondary Payor
          </Text>
          {hasSecondary ? (
            <>
              {secondaryProviderName && (
                <Text size="sm" fw={700}>
                  {secondaryProviderName}
                </Text>
              )}
              {secondaryPlanType && (
                <Text size="xs" c="dimmed">
                  Plan: {secondaryPlanType.name}
                </Text>
              )}
              <InsuranceDetails insurance={secondary} />
            </>
          ) : (
            <Text size="sm" fw={500} fs="italic" c="dimmed">
              None provided
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function InsuranceDetails({ insurance }: { insurance: any }) {
  if (!insurance) return null;

  const details: string[] = [];
  if (insurance.policyNumber) details.push(`Policy: ${insurance.policyNumber}`);
  if (insurance.groupNumber) details.push(`Group #: ${insurance.groupNumber}`);

  return (
    <>
      {details.length > 0 && (
        <Text size="xs" c="dimmed">
          {details.join(" | ")}
        </Text>
      )}
      {insurance.groupName && (
        <Text size="xs" c="dimmed">
          Group Name: {insurance.groupName}
        </Text>
      )}
      {insurance.patientRelationToInsured && (
        <Text size="xs" c="dimmed">
          Relation: {insurance.patientRelationToInsured}
        </Text>
      )}
      {insurance.authorizationNumber && (
        <Text size="xs" c="dimmed">
          Auth #: {insurance.authorizationNumber}
        </Text>
      )}
      {insurance.address && (
        <Text size="xs" c="dimmed">
          {[
            insurance.address.address,
            insurance.address.city,
            insurance.address.state,
            insurance.address.zipCode,
          ]
            .filter(Boolean)
            .join(", ")}
        </Text>
      )}
    </>
  );
}
